<script setup>
import ProgramManagerV2SessionsTable from "./ProgramManagerV2SessionsTable.vue";
import { computed, ref, watch, onMounted, reactive } from "vue";
import sessionFilterDrawer from "./algolia/SessionFilterDrawer.vue";
import { searchClient } from "@/util/searchClient";
import CustomSearchIcon from "@/components/svg-icons/CustomSearchIcon.vue";
import DashboardCard from "@/components/MyStudents/Display/DashboardCard.vue";
import { sessionStatus as SESSION_STATUSES } from "@/components/program-manager/sessions/constants";
import { PROGRAM_MANAGER_SESSION_INDEX, PROGRAM_MANAGER_LINKS, SESSION_FILTER_TABS } from "./constants.js";
import {
  AisInstantSearch,
  AisSearchBox,
  AisConfigure,
  AisInfiniteHits,
  AisStateResults,
} from "vue-instantsearch/vue3/es";
import SessionFilters from "./algolia/SessionFilters.vue";

import { useTenantEntityManager } from "@/composables/useTenantManager.js";
import ProgramManagerV2HeadingMenu from "./ProgramManagerV2HeadingMenu.vue"
import { datadogLogs } from "@datadog/browser-logs";
import SessionsDashboardCards from "./SessionsDashboardCards.vue";
import { useSessionV2Filters } from "@/composables/useSessionV2Filters.js";

defineOptions({
  name: "ProgramManagerV2",
});

const algoliaIndex = computed(() => PROGRAM_MANAGER_SESSION_INDEX);

const helperRef = ref(undefined);

const dashboardCardsRef = ref(undefined);

const onSearch = (helper) => {
  helperRef.value = helper;
  dashboardCardsRef.value?.fetchTabCounts(helper);
  helper.search();
};

const {
  mySessionsFilter,
  catalogSessionsFilter,
  hiddenSessionsFilter,
  selectedEntityId,
  selectedEntity,
} = useSessionV2Filters();

const currentActiveFilter = ref(mySessionsFilter.value);

const updateActiveFilter = (newFilter) => {
  currentActiveFilter.value = newFilter;
};

watch(selectedEntityId, () => {
  if (helperRef.value) {
    helperRef.value.setPage(0).search();
  }
});

watch(currentActiveFilter, () => {
  if (helperRef.value) {
    helperRef.value.setPage(0).search();
  }
});
</script>

<template>
  <div class="p-4 bg-gray-30" v-if="currentActiveFilter">
    <ProgramManagerV2HeadingMenu headingTitle="Sessions"/>
    <SessionsDashboardCards
      ref="dashboardCardsRef"
      :search-client="searchClient"
      :algoliaIndex="algoliaIndex"
      :on-filter-update="updateActiveFilter"
    />
    <ais-instant-search
      :index-name="algoliaIndex"
      :search-client="searchClient"
      :future="{ preserveSharedStateOnUnmount: true }"
      :search-function="onSearch"
    >
      <ais-configure :filters="currentActiveFilter" />
      <ais-state-results v-slot="{ status }">
        <div class="flex flex-wrap justify-end items-center pt-4">
          <!-- Right Group -->
            <ais-search-box>
              <template #default="{ currentRefinement, refine }">
                <div class="flex relative">
                  <input
                    type="search"
                    :value="currentRefinement"
                    class="font-montserrat font-medium text-base leading-5 py-1 pl-7 pr-10 border border-[#D9D9D9] rounded-full h-10 w-full placeholder-[#919D8D]"
                    @input="refine($event.currentTarget.value)"
                  />
                  <CustomSearchIcon
                    data-testid="search-bar-icon"
                    size="20"
                    stroke="#007f80"
                    class="self-center absolute right-4"
                  />
                </div>
              </template>
            </ais-search-box>
            <button
              class="flex justify-center items-center h-10 px-5 font-semibold uppercase text-white bg-university-secondary border border-university-secondary ml-2"
            >
              <span class="inline-block text-base">+ NEW</span>
            </button>
        </div>
        <div hidden>
          <session-filters />
        </div>
        <session-filter-drawer />
        <ais-infinite-hits v-slot="{ items, refineNext, isLastPage }">
          <program-manager-v2-sessions-table
            :results="items"
            :refineNext="refineNext"
            :isLastPage="isLastPage"
            :status="status"
          />
        </ais-infinite-hits>
      </ais-state-results>
    </ais-instant-search>
  </div>
</template>
